<script setup lang="ts">
import { computed } from 'vue';
import BaseCheckbox from '@/shared/components/BaseCheckbox.vue';
import AllocationsPerPhaseLayout from './AllocationsPerPhaseLayout.vue';
import type {
  AllocGroupIndex,
  AllocPhaseIndex,
  AllocPerPhase,
} from '../../models/SolshareSystemSpec';
import { ALGORITHM_TYPE } from '@/utils/constants';

const props = defineProps<{
  phaseIndex: AllocPhaseIndex;
  phaseAllocations: AllocPerPhase;
  algorithmType: ALGORITHM_TYPE;
  showAsKwp: boolean;
  kwpPerPhase: number;
}>();

const emit = defineEmits<{
  (
    e: 'updateEnableValue',
    phaseIndex: AllocPhaseIndex,
    groupIndex: AllocGroupIndex,
    value: boolean,
  ): void;
}>();

const groupIndexes = computed(() => {
  return Object.keys(props.phaseAllocations).map((key) => {
    return Number(key) as AllocGroupIndex;
  });
});

const terminalLabel = (phaseIndex: AllocPhaseIndex, groupIndex: AllocGroupIndex) => {
  return `L${Number(phaseIndex) + 1}-${Number(groupIndex) + 1}`;
};

const isCustomAllocation = computed(() => {
  return props.algorithmType === ALGORITHM_TYPE.CUSTOM_ALLOCATION;
});

const terminalNotConnected = (groupIndex: AllocGroupIndex) => {
  return !props.phaseAllocations[groupIndex]?.connected;
};

const allocationsRemainingValue = computed(() => {
  const allocated = Object.values(props.phaseAllocations).reduce(
    (acc, item) => (acc as number) + (item.allocationValue as number),
    0,
  );
  if (props.showAsKwp) {
    return Number((props.kwpPerPhase - (allocated as number)).toFixed(2));
  } else {
    return Number((100 - (allocated as number)).toFixed(2));
  }
});

const allocationValueText = (phaseIndex: AllocPhaseIndex, groupIndex: AllocGroupIndex) => {
  return props.phaseAllocations[groupIndex]?.allocationValue;
};

const handleEnableChange = (groupIndex: AllocGroupIndex, value: boolean) => {
  emit('updateEnableValue', props.phaseIndex, groupIndex, value);
};

const allocationUnitSymbol = props.showAsKwp
  ? ' kWp' // Add space before the value, e.g. 4.30 kWp
  : '%'; // No space before the value, e.g. 93.30%
</script>

<template>
  <AllocationsPerPhaseLayout
    :phaseAllocations="phaseAllocations"
    :phaseIndex="phaseIndex"
    :groupIndexes="groupIndexes"
    :displayAllocationRemaining="isCustomAllocation"
    :allocationsRemainingValue="allocationsRemainingValue"
    :class="['allocations-enable-container', !isCustomAllocation ? 'demand-based' : '']"
    :showAsKwp="showAsKwp"
  >
    <template #rightCell="{ phaseIndex, groupIndex }">
      <a-row class="enable-connection-row">
        <a-col align="stretch" :span="12" class="col-cell allocation-value-cell">
          <template v-if="terminalNotConnected(groupIndex)"> - </template>
          <template v-else-if="algorithmType === ALGORITHM_TYPE.DEMAND_BASED_DELIVERY">
            <span class="demand-allocation-text">Demand</span>
          </template>
          <template v-else>
            {{ allocationValueText(phaseIndex, groupIndex) }}{{ allocationUnitSymbol }}
          </template>
        </a-col>
        <a-col :span="12" class="col-cell">
          <template v-if="terminalNotConnected(groupIndex)"> - </template>
          <template v-else>
            <BaseCheckbox
              :name="`enable-checkbox-${terminalLabel(phaseIndex, groupIndex)}`"
              :modelValue="props.phaseAllocations[groupIndex].enable"
              :data-tn="`enable-checkbox-${terminalLabel(phaseIndex, groupIndex)}`"
              :ariaLabel="`Enable/disable connection for ${terminalLabel(phaseIndex, groupIndex)}`"
              @update:modelValue="(value) => handleEnableChange(groupIndex, value)"
            />
          </template>
        </a-col>
      </a-row>
    </template>

    <template #errorBlock v-if="allocationsRemainingValue !== 0">
      Remaining value must be 0{{ allocationUnitSymbol }}
    </template>
  </AllocationsPerPhaseLayout>
</template>

<style lang="less" scoped>
.allocations-enable-container {
  :deep(.right-cell-container:not(.alloc-remaining__value)) {
    padding-left: 0;
    padding-right: 0;
    border-top: 1px solid @color-mid-tone;

    &.last-row {
      border-bottom: 1px solid @color-mid-tone;
    }
  }

  :deep(.connection-name-cell) {
    background-color: @color-background-dark;
  }

  :deep(.right-cell-container.alloc-remaining__value) {
    justify-content: start;
  }
}

.enable-connection-row.ant-row {
  width: 100%;
  height: 100%;
}

.col-cell {
  display: flex;
  align-items: center;
  justify-content: center;

  &.allocation-value-cell {
    background-color: @color-background-dark;
    text-align: center;
    line-height: normal;

    .demand-allocation-text {
      font-size: 0.8rem;
      font-weight: @font-weight-medium;
    }
  }
}
</style>
